import { useEffect, useState } from "react";
import type { Experience, Project } from "../types";

// Live content overlay: a best-effort fetch of admin edits (DynamoDB via the content API) merged
// over the baked YAML. Env-gated — with no VITE_CONTENT_API_URL the site renders exactly as the
// bundled content, so nothing changes until the ApiStack is deployed and the URL is wired in.

const API = (import.meta.env.VITE_CONTENT_API_URL ?? "").replace(/\/$/, "");
export const overlayEnabled = Boolean(API);

export type Stage = "draft" | "in-review" | "published" | "archived";

interface Override {
  stage?: Stage;
  fields?: Record<string, string>;
}

const store = new Map<string, Override>();

// When an admin is signed in, listings reveal every stage (with a badge) so drafts are reachable.
let adminMode = false;
export function setAdminMode(on: boolean): void {
  if (adminMode !== on) {
    adminMode = on;
    window.dispatchEvent(new Event("overlay-change"));
  }
}
export const isAdminMode = (): boolean => adminMode;

/** Fetch the PUBLIC overlay (published field overrides + every edited item's stage). Best-effort. */
export async function loadOverlay(): Promise<void> {
  if (!API) return;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 2500); // never block first paint on a slow API
  try {
    // no-store: this overlay carries the live `config-active` kill-switch, so it must never be
    // replayed from the browser's 60s HTTP cache (`Cache-Control: public, max-age=60`). Mirrors the
    // baked-config read in appConfig.ts — without it, a re-enable (interactive=true) can't take
    // effect on reload until the cached `false` expires, so the site stays dark for up to a minute.
    const res = await fetch(`${API}/content/overrides`, { cache: "no-store", signal: ctrl.signal });
    if (!res.ok) return;
    const data = (await res.json()) as {
      items?: { entityId: string; stage?: Stage; fields?: Override["fields"] }[];
    };
    store.clear();
    for (const it of data.items ?? []) store.set(it.entityId, { stage: it.stage, fields: it.fields });
  } catch {
    // overlay is best-effort — the baked content is the fallback
  } finally {
    clearTimeout(timer);
    window.dispatchEvent(new Event("overlay-change"));
  }
}

const STAGE_VALUES: readonly string[] = ["draft", "in-review", "published", "archived"];

/**
 * Effective stage: a live overlay stage (admin edit) wins; otherwise the baked YAML status is
 * honored verbatim when it is one of the four known stages, falling back to "published" for absent
 * or legacy values. This lets the reconcile script make YAML the full source of truth (a baked
 * "in-review"/"archived" hides publicly) even after the overlay is cleared.
 */
export function getStage(projectId: string, bakedStatus: string | undefined): Stage {
  const overlay = store.get(projectId)?.stage;
  if (overlay) return overlay;
  return STAGE_VALUES.includes(bakedStatus ?? "") ? (bakedStatus as Stage) : "published";
}

/** Effective publication state. Admins see every stage (managed via a badge), so it returns true. */
export function isPublished(projectId: string, bakedStatus: string | undefined): boolean {
  if (adminMode) return true;
  return getStage(projectId, bakedStatus) === "published";
}

/** Read the stored override fields for a document-style record (e.g. the About page). */
export function getDocFields(id: string): Record<string, string> {
  return store.get(id)?.fields ?? {};
}

// Tag fields (industries/technologies/skills) are arrays, so the editor stores them JSON-encoded in
// the string-map overlay. Parse leniently (JSON array, else comma-separated) so a hand-set value works.
function parseTags(v: string | undefined): string[] {
  if (v == null) return [];
  try {
    const arr = JSON.parse(v);
    if (Array.isArray(arr)) return arr.map(String);
  } catch {
    /* not JSON — fall back to comma-separated */
  }
  return v.split(",").map((s) => s.trim()).filter(Boolean);
}

/** Apply published field overrides onto a project (no-op when there are none). */
export function applyOverride(p: Project): Project {
  const f = store.get(p.id)?.fields;
  if (!f) return p;
  return {
    ...p,
    ...(f.title ? { title: f.title } : {}),
    ...(f.summary ? { summary: f.summary } : {}),
    ...(f.narrative ? { narrative: f.narrative } : {}),
    ...(f.client ? { client: f.client } : {}),
    ...(f.clientAnonymized ? { clientAnonymized: f.clientAnonymized } : {}),
    ...(f.industries ? { industries: parseTags(f.industries) } : {}),
    ...(f.technologies ? { technologies: parseTags(f.technologies) } : {}),
    ...(f.skills ? { skills: parseTags(f.skills) } : {}),
  };
}

/** Apply field overrides onto an experience (company/role/summary/narrative + tag arrays). */
export function applyExperienceOverride(e: Experience): Experience {
  const f = store.get(e.id)?.fields;
  if (!f) return e;
  return {
    ...e,
    ...(f.company ? { company: f.company } : {}),
    ...(f.role ? { role: f.role } : {}),
    ...(f.summary ? { summary: f.summary } : {}),
    ...(f.narrative ? { narrative: f.narrative } : {}),
    ...(f.industries ? { industries: parseTags(f.industries) } : {}),
    ...(f.technologies ? { technologies: parseTags(f.technologies) } : {}),
    ...(f.skills ? { skills: parseTags(f.skills) } : {}),
  };
}

/** Admin write — PUT one engagement's override (stage and/or fields). Updates the local store. */
export async function putOverride(
  id: string,
  body: { stage?: Stage; fields?: Override["fields"] },
  token: string,
): Promise<boolean> {
  if (!API) return false;
  const res = await fetch(`${API}/content/override/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) return false;
  const data = (await res.json()) as { item?: { stage?: Stage; fields?: Override["fields"] } };
  if (data.item) store.set(id, { stage: data.item.stage, fields: data.item.fields });
  window.dispatchEvent(new Event("overlay-change"));
  return true;
}

export interface ConnectContacts {
  emails: { address: string; displayName?: string }[];
  phones: { number: string; type?: string }[];
  errors?: { emails?: string; phones?: string };
}

/**
 * Admin read — list the Connect instance's email addresses + phone numbers for the config picker.
 * Best-effort: returns null when the overlay API isn't wired or the request fails, so the picker
 * degrades to free-text.
 */
export async function fetchConnectContacts(token: string): Promise<ConnectContacts | null> {
  if (!API) return null;
  try {
    const res = await fetch(`${API}/connect/contacts`, {
      headers: { authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    return (await res.json()) as ConnectContacts;
  } catch {
    return null;
  }
}

// ─── Model Settings (admin: Bedrock model + tuning) ─────────────────────────
export interface AiConfig {
  model: string;
  maxTokens: number;
  temperature: number;
  topP: number | null;
  corpusCap: number;
  systemPreamble: string;
}

export const AI_CONFIG_DEFAULTS: AiConfig = {
  model: "us.anthropic.claude-haiku-4-5-20251001-v1:0",
  maxTokens: 320,
  temperature: 0.2,
  topP: null,
  corpusCap: 80000,
  systemPreamble: "",
};

export interface ModelOption {
  id: string;
  name: string;
}

/** Admin: list the account's active Anthropic inference profiles (for the model dropdown). */
export async function fetchModels(token: string): Promise<ModelOption[] | null> {
  if (!API) return null;
  try {
    const res = await fetch(`${API}/admin/models`, { headers: { authorization: `Bearer ${token}` } });
    if (!res.ok) return null;
    const data = (await res.json()) as { models?: ModelOption[] };
    return data.models ?? [];
  } catch {
    return null;
  }
}

/** Admin: probe-invoke a model to confirm it's enabled. Never enables or subscribes. */
export async function testModel(
  token: string,
  modelId: string,
): Promise<{ ok: boolean; notEnabled?: boolean; error?: string }> {
  if (!API) return { ok: false, error: "Content API not configured" };
  try {
    const res = await fetch(`${API}/admin/models/test`, {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
      body: JSON.stringify({ modelId }),
    });
    return (await res.json()) as { ok: boolean; notEnabled?: boolean; error?: string };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "request failed" };
  }
}

/** Re-render the caller on overlay / admin-mode changes. */
export function useOverlayVersion(): number {
  const [v, setV] = useState(0);
  useEffect(() => {
    const on = () => setV((n) => n + 1);
    window.addEventListener("overlay-change", on);
    return () => window.removeEventListener("overlay-change", on);
  }, []);
  return v;
}
